// alertSounds.js
// Web Audio API synthesized sound effects for the alerts overlay. One preset
// per alert type, all built from oscillators + gain envelopes — zero asset
// files, no dependencies.
//
// Sound design: retro 8-bit / 16-bit video game vocabulary. Square waves,
// fast attacks, short releases, simple melodic motifs. Reference points are
// Super Mario Bros / Zelda / Pokemon era: cute, punchy, instantly readable.
// The tip_received preset is a direct nod to the Mario coin sound (B5 → E6).
//
// Public API:
//   playAlertSound(key)  — fire the preset for an alert type, no-op if muted
//                          or if the audio context is suspended
//   getAudioState()      — { state: "running"|"suspended"|"closed", supported }
//   resumeAudio()        — call from a click handler to resume after suspend
//   setMuted(bool)       — global mute flag (set from useStreamConfig)
//
// Note tuning: equal-temperament frequencies in Hz (A4 = 440).

// ── MODULE STATE ─────────────────────────────────────────────────────────────
let _ctx = null;
let _muted = false;
let _reverbNode = null;
let _dryGain = null;
let _wetGain = null;

function ctx() {
  if (_ctx) return _ctx;
  const C = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!C) return null;
  _ctx = new C();
  setupReverb(_ctx);
  return _ctx;
}

// Shared output bus: all sounds connect to output() instead of ctx.destination.
// The bus splits into dry (direct) and wet (convolver reverb) paths.
function output() {
  return _dryGain || ctx()?.destination;
}

function setupReverb(c) {
  // Generate a synthetic impulse response — short, warm room reverb
  const duration = 0.6; // seconds
  const decay = 2.5;    // how fast the reverb fades
  const rate = c.sampleRate;
  const length = Math.floor(rate * duration);
  const impulse = c.createBuffer(2, length, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }

  const convolver = c.createConvolver();
  convolver.buffer = impulse;

  // Dry path (direct signal)
  _dryGain = c.createGain();
  _dryGain.gain.value = 1.0;
  _dryGain.connect(c.destination);

  // Wet path (reverb)
  _wetGain = c.createGain();
  _wetGain.gain.value = 0.40; // warm with space
  _dryGain.connect(convolver);
  convolver.connect(_wetGain);
  _wetGain.connect(c.destination);

  _reverbNode = convolver;
}

export function getAudioState() {
  const c = ctx();
  if (!c) return { state: "unsupported", supported: false };
  return { state: c.state, supported: true };
}

export function resumeAudio() {
  const c = ctx();
  if (c && c.state === "suspended") return c.resume();
  return Promise.resolve();
}

export function setMuted(b) { _muted = !!b; }

// ── NOTE FREQUENCIES (Hz) ────────────────────────────────────────────────────
const N = {
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.00, A4: 440.00, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.00, B5: 987.77,
  C6: 1046.50, D6: 1174.66, E6: 1318.51, F6: 1396.91, G6: 1567.98, A6: 1760.00, B6: 1975.53,
  C7: 2093.00, E7: 2637.02, G7: 3135.96,
};

// ── PRIMITIVES ───────────────────────────────────────────────────────────────
// Single tone with attack/decay/release envelope.
function tone({ freq, start = 0, dur = 0.2, type = "sine", peak = 0.22, attack = 0.005, release = 0.06 }) {
  const c = ctx();
  if (!c) return;
  const t0 = c.currentTime + start;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + attack);
  g.gain.setValueAtTime(peak, t0 + Math.max(attack, dur - release));
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g);
  g.connect(output());
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

// ── PRESETS ──────────────────────────────────────────────────────────────────
// All retro 8-bit feel. Square waves dominate; triangle for occasional warmth.
// Short envelopes (5ms attack, 30-50ms release) keep the punchy NES character.
const SQ = "square";
const TR = "triangle";

const PRESETS = {
  // 🎟️ New order — "item get" 4-note rising arpeggio (Zelda-ish chime)
  new_order() {
    tone({ freq: N.G5, start: 0.00, dur: 0.06, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.C6, start: 0.06, dur: 0.06, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.E6, start: 0.12, dur: 0.06, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.G6, start: 0.18, dur: 0.16, type: SQ, peak: 0.24, attack: 0.002, release: 0.06 });
  },

  // 🔥 Queue milestone — quick rising "ding ding"
  queue_milestone() {
    tone({ freq: N.E6, start: 0.00, dur: 0.07, type: SQ, peak: 0.24, attack: 0.002, release: 0.04 });
    tone({ freq: N.G6, start: 0.10, dur: 0.10, type: SQ, peak: 0.26, attack: 0.002, release: 0.05 });
  },

  // 🚫 Queue full — Mario "bonk" descending raspberry
  queue_full() {
    tone({ freq: N.A5,  start: 0.00, dur: 0.08, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.F5,  start: 0.08, dur: 0.08, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.D5,  start: 0.16, dur: 0.16, type: SQ, peak: 0.22, attack: 0.002, release: 0.06 });
  },

  // ✅ Queue open — 6-note power-up cascade (1-up adjacent)
  queue_open() {
    tone({ freq: N.C5, start: 0.00, dur: 0.05, type: SQ, peak: 0.20, attack: 0.002, release: 0.03 });
    tone({ freq: N.E5, start: 0.05, dur: 0.05, type: SQ, peak: 0.20, attack: 0.002, release: 0.03 });
    tone({ freq: N.G5, start: 0.10, dur: 0.05, type: SQ, peak: 0.21, attack: 0.002, release: 0.03 });
    tone({ freq: N.C6, start: 0.15, dur: 0.05, type: SQ, peak: 0.22, attack: 0.002, release: 0.03 });
    tone({ freq: N.E6, start: 0.20, dur: 0.05, type: SQ, peak: 0.23, attack: 0.002, release: 0.03 });
    tone({ freq: N.G6, start: 0.25, dur: 0.18, type: SQ, peak: 0.25, attack: 0.002, release: 0.06 });
  },

  // ✨ Pancake done — Zelda "treasure get" fanfare
  pancake_done() {
    tone({ freq: N.G5, start: 0.00, dur: 0.08, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.C6, start: 0.08, dur: 0.08, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.E6, start: 0.16, dur: 0.08, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.G6, start: 0.24, dur: 0.32, type: SQ, peak: 0.24, attack: 0.002, release: 0.10 });
    // Soft triangle bass under the held high note for warmth
    tone({ freq: N.C5, start: 0.24, dur: 0.32, type: TR, peak: 0.14, attack: 0.005, release: 0.10 });
  },

  // 🌟 New sub — "1-up mushroom" triumphant motif
  // Loose homage to Mario's 1-up: E G E C D G — quick + bouncy
  new_sub() {
    tone({ freq: N.E6, start: 0.00, dur: 0.07, type: SQ, peak: 0.24, attack: 0.002, release: 0.04 });
    tone({ freq: N.G6, start: 0.07, dur: 0.07, type: SQ, peak: 0.24, attack: 0.002, release: 0.04 });
    tone({ freq: N.E6, start: 0.14, dur: 0.07, type: SQ, peak: 0.24, attack: 0.002, release: 0.04 });
    tone({ freq: N.C6, start: 0.21, dur: 0.07, type: SQ, peak: 0.24, attack: 0.002, release: 0.04 });
    tone({ freq: N.D6, start: 0.28, dur: 0.07, type: SQ, peak: 0.24, attack: 0.002, release: 0.04 });
    tone({ freq: N.G6, start: 0.35, dur: 0.22, type: SQ, peak: 0.26, attack: 0.002, release: 0.08 });
  },

  // 👋 New follow — soft 2-note menu chime
  new_follow() {
    tone({ freq: N.C6, start: 0.00, dur: 0.07, type: SQ, peak: 0.20, attack: 0.002, release: 0.04 });
    tone({ freq: N.E6, start: 0.08, dur: 0.14, type: SQ, peak: 0.22, attack: 0.002, release: 0.05 });
  },

  // 💛 Tip received — Mario coin (B5 → E6, square wave, very short)
  // The actual SMB coin sound: short B5 followed by sustained higher E6.
  tip_received() {
    tone({ freq: N.B5, start: 0.00, dur: 0.04, type: SQ, peak: 0.24, attack: 0.001, release: 0.02 });
    tone({ freq: N.E6, start: 0.04, dur: 0.20, type: SQ, peak: 0.26, attack: 0.001, release: 0.06 });
  },

  // 📢 Custom announcement — 3-note text-box chime (rising)
  custom_announcement() {
    tone({ freq: N.E5, start: 0.00, dur: 0.05, type: SQ, peak: 0.22, attack: 0.002, release: 0.03 });
    tone({ freq: N.G5, start: 0.06, dur: 0.05, type: SQ, peak: 0.22, attack: 0.002, release: 0.03 });
    tone({ freq: N.B5, start: 0.12, dur: 0.16, type: SQ, peak: 0.24, attack: 0.002, release: 0.06 });
  },

  // 🥞 Shift started — "stage start" 5-note ascending arpeggio
  shift_started() {
    tone({ freq: N.C5, start: 0.00, dur: 0.07, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.E5, start: 0.07, dur: 0.07, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.G5, start: 0.14, dur: 0.07, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.C6, start: 0.21, dur: 0.07, type: SQ, peak: 0.23, attack: 0.002, release: 0.04 });
    tone({ freq: N.E6, start: 0.28, dur: 0.22, type: SQ, peak: 0.25, attack: 0.002, release: 0.08 });
  },

  // 👋 Shift ended — "stage clear" descending arpeggio
  shift_ended() {
    tone({ freq: N.E6, start: 0.00, dur: 0.08, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.C6, start: 0.08, dur: 0.08, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.G5, start: 0.16, dur: 0.08, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.E5, start: 0.24, dur: 0.08, type: SQ, peak: 0.22, attack: 0.002, release: 0.04 });
    tone({ freq: N.C5, start: 0.32, dur: 0.22, type: SQ, peak: 0.22, attack: 0.002, release: 0.08 });
  },
};

// ── PUBLIC PLAY ──────────────────────────────────────────────────────────────
export function playAlertSound(key) {
  if (_muted) return;
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") {
    // Browser autoplay block — caller should have shown the click-to-enable
    // prompt. Don't try to resume here (must come from a user gesture).
    return;
  }
  const preset = PRESETS[key];
  if (!preset) return;
  try { preset(); }
  catch (err) { console.error("[alertSounds] play failed:", err); }
}
